import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';
import { VideoCreatorResponseDto, VideoResponseDto } from '../../common/general-types/videos.dto';
import { VideoService } from '../../services/video/video.service';
import { SuperSocialService } from '../../services/super-social/super-social.service';
import { FormatService } from '../../common/format/format.service';
import { MgcPortfolioInlineVideoComponent } from '../../pages/creators/molecules/creator-profile/mgc-portfolio/mgc-portfolio-inline-video.component';
import { PreloaderV2Component } from '../preloader-v2/preloader-v2.component';
import { Router } from '@angular/router';
import { ViewportOverlayDirective } from '../../common/directives/viewport-overlay/viewport-overlay.directive';

type CarouselVideo = VideoCreatorResponseDto | VideoResponseDto;

@Component({
  selector: 'atom-video-carousel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MgcPortfolioInlineVideoComponent, PreloaderV2Component],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './video-carousel.component.html',
  styleUrl: './video-carousel.component.scss',
})
export class VideoCarouselComponent implements OnInit, OnChanges {
  @Input({ required: true }) video!: CarouselVideo;
  @Input() playlist: CarouselVideo[] = [];
  @Input() showFooter = false;

  @Output() closePreviewOut = new EventEmitter<void>();
  @Output() videoChange = new EventEmitter<CarouselVideo>();

  readonly displayedVideo = signal<CarouselVideo | undefined>(undefined);
  readonly playlistPositionLabel = signal<string | null>(null);
  readonly slideLoading = signal(false);

  isPlaying = signal(false);
  audio = new Audio();

  summary = signal<{
    content_summary: any;
    platform: string;
    post: any;
    video_file: any;
  } | undefined>(undefined);

  private readonly superSocialService = inject(SuperSocialService);
  readonly formatService = inject(FormatService);
  readonly videoService = inject(VideoService);
  private readonly router = inject(Router);
  private activeTransitionId = 0;

  @HostListener('document:keydown.escape')
  handleEscapeKey(): void {
    this.close();
  }

  @HostListener('document:keydown', ['$event'])
  handleArrowKeys(event: KeyboardEvent): void {
    if (!this.hasPlaylistNavigation()) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToPreviousVideo();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToNextVideo();
    }
  }

  ngOnInit(): void {
    this.displayedVideo.set(this.video);
    this.updatePlaylistPositionLabel();
    this.loadSlideContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['video']?.currentValue) {
      this.displayedVideo.set(changes['video'].currentValue as CarouselVideo);
      this.resetPlayback();
      this.updatePlaylistPositionLabel();
      this.loadSlideContent();
      return;
    }

    if (changes['playlist']) {
      this.updatePlaylistPositionLabel();
    }
  }

  hasPlaylistNavigation(): boolean {
    return this.playlist.length > 1;
  }

  canGoPrevious(): boolean {
    return this.getCurrentPlaylistIndex() > 0;
  }

  canGoNext(): boolean {
    const index = this.getCurrentPlaylistIndex();
    return index >= 0 && index < this.playlist.length - 1;
  }

  goToPreviousVideo(): void {
    const index = this.getCurrentPlaylistIndex();
    if (index <= 0) {
      return;
    }

    this.selectVideo(this.playlist[index - 1]);
  }

  goToNextVideo(): void {
    const index = this.getCurrentPlaylistIndex();
    if (index < 0 || index >= this.playlist.length - 1) {
      return;
    }

    this.selectVideo(this.playlist[index + 1]);
  }

  private loadSlideContent(): void {
    const currentVideo = this.displayedVideo();
    if (!currentVideo) {
      return;
    }

    const transitionId = ++this.activeTransitionId;
    this.slideLoading.set(true);

    void this.preloadThumbnail(currentVideo).then(() => {
      if (transitionId !== this.activeTransitionId) {
        return;
      }

      this.slideLoading.set(false);
    });

    void firstValueFrom(
      this.superSocialService.getContentSumaryByFullUrl(currentVideo.fullUrl).pipe(
        catchError(() => of(undefined)),
      ),
    ).then((summary) => {
      if (transitionId !== this.activeTransitionId) {
        return;
      }

      this.summary.set(summary);
    });
  }

  private preloadThumbnail(video: CarouselVideo): Promise<void> {
    const thumbnailUrl = video.thumbnailURL;
    if (!thumbnailUrl) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => resolve();
      image.onerror = () => resolve();
      image.src = thumbnailUrl;
    });
  }

  togglePlayPause(): void {
    const musicInfo = this.summary()?.video_file?.musicInfo;
    if (!this.isPlaying() && musicInfo?.music) {
      this.audio.src = musicInfo.music;
      this.audio.load();
      this.audio.play();
      this.isPlaying.set(true);

      this.audio.onended = () => {
        this.isPlaying.set(false);
      };
    } else {
      this.audio.pause();
      this.isPlaying.set(false);
    }
  }

  goToBrief(): void {
    const currentVideo = this.displayedVideo();
    if (currentVideo?.briefId) {
      this.router.navigate(['/manage-brief', currentVideo.briefId, '1']);
    }
  }

  viewSubmission(): void {
    const currentVideo = this.displayedVideo();
    if (currentVideo?.briefId && currentVideo.creatorId) {
      this.router.navigate(['/view-submission', currentVideo.briefId, currentVideo.creatorId]);
    }
  }

  close(): void {
    this.resetPlayback();
    this.closePreviewOut.emit();
  }

  private selectVideo(video: CarouselVideo): void {
    this.displayedVideo.set(video);
    this.resetPlayback();
    this.updatePlaylistPositionLabel();
    this.videoChange.emit(video);
    this.loadSlideContent();
  }

  private getCurrentPlaylistIndex(): number {
    const current = this.displayedVideo();
    if (!this.playlist.length || !current) {
      return -1;
    }

    return this.playlist.findIndex((item) => item.id === current.id);
  }

  private updatePlaylistPositionLabel(): void {
    const index = this.getCurrentPlaylistIndex();
    if (this.playlist.length <= 1 || index < 0) {
      this.playlistPositionLabel.set(null);
      return;
    }

    this.playlistPositionLabel.set(`${index + 1} / ${this.playlist.length}`);
  }

  private resetPlayback(): void {
    this.audio.pause();
    this.isPlaying.set(false);
  }
}
